import * as Sentry from '@sentry/react'
import { makeAutoObservable } from 'mobx'
import { apiClient } from 'api/trpc/trpc-client'
import { snackbarError, snackbarInfo, snackbarSuccess } from 'reusable/mui/snackbar/snackbar'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore'
import AnnouncementStore from 'features/main/main-router/org-guard/main-container/announcement/AnnouncementStore'
import CacheStore from 'cache/CacheStore'
import { ActiveIntegrations, Analytics } from 'analytics/analytics'

export type XpmConnectionStatus = 'unknown' | 'not connected' | 'connecting' | 'connected' | 'disconnecting'

const CALLBACK_PARAMS = ['xeroCode', 'xeroState', 'xeroError']

interface XpmConnectionStoreProps {
   orgGuardStore: OrgGuardStore
   announcementStore: AnnouncementStore
   cacheStore: CacheStore
}

/** Reads the XPM OAuth callback params from the current URL without consuming them. */
const readCallbackParams = () => {
   const queryString = new URLSearchParams(window.location.search)
   return {
      code: queryString.get('xeroCode') ?? undefined,
      state: queryString.get('xeroState') ?? undefined,
      error: queryString.get('xeroError') ?? undefined,
   }
}

/** Strips the XPM callback params, leaving any unrelated query params on the URL. */
const clearCallbackParams = () => {
   const queryString = new URLSearchParams(window.location.search)
   CALLBACK_PARAMS.forEach(param => queryString.delete(param))
   const newQueryString = queryString.toString()
   const newUrl = `${window.location.pathname}${newQueryString ? `?${newQueryString}` : ''}${window.location.hash}`
   // Replace rather than push so browser-back can't restore a URL holding a spent code.
   window.history.replaceState({ path: newUrl }, '', newUrl)
}

/**
 * Owns the org's XPM connection and client-sync state for every surface that shows it.
 *
 * Created once by MainContainer, so redeeming an OAuth callback never depends on which
 * modal happens to be mounted when the user returns from Xero.
 */
export default class XpmConnectionStore {
   sp
   /**
    * Seeded to 'connecting' when the URL already carries a callback, so the first frame
    * on every surface reads as busy instead of offering a Connect button mid-exchange.
    */
   status: XpmConnectionStatus
   isSyncing = false
   /** True while the org is being switched to XPM as its client source. */
   settingSource = false
   /** True for the page load that lands back from Xero, so an interrupted flow can reopen itself. */
   readonly returnedFromConnect: boolean
   private redeemed = false

   constructor(sp: XpmConnectionStoreProps) {
      this.sp = sp
      this.returnedFromConnect = !!readCallbackParams().code
      this.status = this.returnedFromConnect ? 'connecting' : 'unknown'
      makeAutoObservable(this)
   }

   get org() {
      return this.sp.orgGuardStore.userOrg
   }

   get xeroAuthorised() {
      return !!this.org?.xeroAuthorised
   }

   get xpmName() {
      return this.org?.xpmName
   }

   /** True while connecting or disconnecting — surfaces render a loading state rather than an action. */
   get busy() {
      return this.status === 'connecting' || this.status === 'disconnecting'
   }

   get cardStatus(): 'connected' | 'not connected' {
      if (this.status === 'not connected') {
         return 'not connected'
      }
      return this.xeroAuthorised ? 'connected' : 'not connected'
   }

   /** Redeems a pending OAuth callback once per page load, then starts the client import. */
   redeemCallbackIfPresent = async () => {
      if (this.redeemed) return
      const { code, state, error } = readCallbackParams()

      if (error) {
         this.redeemed = true
         clearCallbackParams()
         this.status = 'not connected'
         if (error === 'access_denied') {
            snackbarInfo('Integration unsuccessful. Please try again.')
         } else {
            snackbarError('Xero XPM integration error', { error })
         }
         return
      }

      if (!code || !state) return

      this.redeemed = true
      clearCallbackParams()
      await this.completeConnection({ code, state })
   }

   /** Starts an incremental client sync, optionally only when XPM is already the connected client source. */
   runSync = async (params?: { ifConnected?: boolean }) => {
      if (params?.ifConnected && (!this.xeroAuthorised || this.org?.syncVia !== 'xpm')) return
      if (this.isSyncing) return

      this.isSyncing = true
      try {
         const result = await apiClient.xpm.updateClientsFromXPM.mutate()
         if (result === 'not_client_source' || result === 'disconnected') {
            this.isSyncing = false
            return
         }
         await this.subscribeToSyncCompletion()
      } catch (error: unknown) {
         // Released here only: on the happy path the websocket callback owns the reset.
         this.isSyncing = false
         Sentry.captureException(error, {
            tags: { component: 'XpmConnectionStore', action: 'runSync' },
         })
         snackbarError('We had trouble starting the XPM sync. Please try again.')
      }
   }

   /** Makes XPM the client source and shows its first full sync running until the completion event arrives. */
   becomeClientSource = async () => {
      this.settingSource = true
      try {
         const result = await apiClient.clientSource.set.mutate({ source: 'xpm' })
         this.sp.orgGuardStore.updateUserOrgData({ syncVia: 'xpm' })
         if (result.queued) {
            this.isSyncing = true
            await this.subscribeToSyncCompletion()
         }
         snackbarSuccess(result.queued ? 'Clients will now sync from XPM, and the first sync has started' : 'Clients will now sync from XPM')
      } catch (error: unknown) {
         Sentry.captureException(error, {
            tags: { component: 'XpmConnectionStore', action: 'becomeClientSource' },
         })
         snackbarError('We had trouble switching the client source to XPM. Please try again.')
      } finally {
         this.settingSource = false
      }
   }

   setConnected = (xpmName: string) => {
      this.status = 'connected'
      this.writeOrgConnection(xpmName)
      if (this.sp.orgGuardStore.orgId) {
         Analytics.setActiveIntegrations(this.sp.orgGuardStore.orgId, { [ActiveIntegrations.XPM]: true })
      }
   }

   setDisconnecting = () => {
      this.status = 'disconnecting'
   }

   /** Leaves the disconnecting state without claiming a result, so the card reflects the org again. */
   setDisconnectFailed = () => {
      this.status = this.xeroAuthorised ? 'connected' : 'not connected'
   }

   setDisconnected = () => {
      this.status = 'not connected'
      this.writeOrgConnection(undefined)
      if (this.sp.orgGuardStore.orgId) {
         Analytics.setActiveIntegrations(this.sp.orgGuardStore.orgId, { [ActiveIntegrations.XPM]: false })
      }
   }

   private writeOrgConnection = (xpmName: string | undefined) => {
      if (this.sp.orgGuardStore.userOrg) {
         this.sp.orgGuardStore.userOrg.xpmName = xpmName
         this.sp.orgGuardStore.userOrg.xeroAuthorised = !!xpmName
         // Connecting makes XPM the client source unless another integration holds it; disconnecting releases it only while XPM holds it.
         if (xpmName) {
            this.sp.orgGuardStore.userOrg.syncVia = this.sp.orgGuardStore.userOrg.syncVia ?? 'xpm'
         } else if (this.sp.orgGuardStore.userOrg.syncVia === 'xpm') {
            this.sp.orgGuardStore.userOrg.syncVia = undefined
         }
      }
   }

   private completeConnection = async (xeroParams: { code: string; state: string }) => {
      this.status = 'connecting'

      let xpmName: string | undefined
      try {
         xpmName = await apiClient.xpm.setTokenSetAndGetXpmName.mutate({ xeroParams })
      } catch (error: unknown) {
         this.status = 'not connected'
         Sentry.captureException(error, {
            tags: { component: 'XpmConnectionStore', action: 'completeConnection' },
         })
         snackbarError('We had trouble connecting to XPM. Please try again.')
         return
      }

      if (!xpmName) {
         this.status = 'not connected'
         snackbarError('We had trouble connecting to XPM. Please try again.')
         return
      }

      const xpmIsClientSource = (this.org?.syncVia ?? 'xpm') === 'xpm'
      this.setConnected(xpmName)
      await this.dismissDisconnectedAnnouncement()

      if (!xpmIsClientSource) {
         snackbarSuccess('XPM successfully connected')
         return
      }
      this.isSyncing = true
      await this.subscribeToSyncCompletion()
      this.startImport()
      snackbarSuccess('XPM successfully connected & clients sync started')
   }

   private dismissDisconnectedAnnouncement = async () => {
      try {
         await this.sp.announcementStore.fetchAnnouncements()
         if (this.sp.announcementStore.hasXpmConnectedAnnouncement) {
            this.sp.announcementStore.dismissAnnouncement('xpmDisconnected')()
         }
      } catch (error: unknown) {
         Sentry.captureException(error, {
            tags: { component: 'XpmConnectionStore', action: 'dismissDisconnectedAnnouncement' },
         })
      }
   }

   /** Full import, run once after a fresh connect. */
   private startImport = async () => {
      try {
         await apiClient.xpm.importClientsFromXPM.mutate()
      } catch (error: unknown) {
         this.isSyncing = false
         Sentry.captureException(error, {
            tags: { component: 'XpmConnectionStore', action: 'startImport' },
         })
         snackbarError('XPM connected but we had trouble syncing your clients. Please try again.')
      }
   }

   private subscribeToSyncCompletion = async () => {
      await this.sp.cacheStore.sp.webSocketStore.addSubscriptions({
         orgId: this.sp.orgGuardStore.userData.userOrg.orgId,
         userId: this.sp.orgGuardStore.userData.id,
         subscriptions: [{
            event: 'manual-xpm-sync-completed',
            entityId: this.sp.orgGuardStore.userData.id,
            callback: async (response) => {
               try {
                  if (response.message === 'sync_failed') {
                     snackbarError('We had trouble syncing your clients from XPM. Please try again.')
                     return
                  }
                  await this.sp.cacheStore.clientsCacheStore.fetch()
                  snackbarSuccess('XPM clients sync completed')
               } catch {
                  snackbarError('XPM clients sync failed')
               } finally {
                  this.isSyncing = false
               }
            }
         }]
      })
   }
}

export const {
   Provider: XpmConnectionStoreProvider,
   useProvidedStore: useXpmConnectionStore,
   useOptionalStore: useOptionalXpmConnectionStore,
} = createStoreContext(XpmConnectionStore)
